/**
 * The Dashboard's tiles (piece 12). 🔨 A tile with no `DashboardTile` row does
 * not appear, and adding a tile means adding a row — the same mechanism
 * `NavItem` and `WorkQueue` already keep. The default arrangement is the seeded
 * `sortRank` order, one order for every role, which a person's own `SavedView`
 * row overrides.
 */
import { canReadModule, canUseCapability } from '../permissions/guards';
import { externalSession } from '../permissions/portal';
import { isModule, type Module } from '../permissions/modules';
import type { GateContext } from '../permissions/guards';
import type { Viewer } from '../permissions/viewer';
import type { WorkQueueRow } from './workQueues';
import { visibleQueues } from './workQueues';
import { DASHBOARD_SCOPE_KEY } from './savedView';

/** Re-exported so a dashboard reader does not have to know the preference store's file. */
export { DASHBOARD_SCOPE_KEY };

export interface DashboardTileRow {
  key: string;
  label: string;
  /** The key a viewer must pass, of the same kind its subject uses. */
  permissionKey: string;
  sortRank: number;
  isActive?: boolean;
}

/**
 * The work-queue tile's own key: the plan gives it no capability of its own —
 * it "passes when the viewer passes any queue's own key" — so the resolver reads
 * this sentinel rather than a rule keyed on the tile's identity.
 */
export const ANY_QUEUE_KEY = 'dashboard.workQueues.any';

/**
 * 🔨 A tile's `permissionKey` is a capability key, or the name of a Module where
 * the tile's page is read under a module rather than a capability (decision
 * 0600). The Sales Reporting dashboard is the first of the second kind: its page
 * answers to a Deal read, and inventing a capability for the tile alone would
 * have been a second gate saying the same thing differently.
 */
export function tileIsVisible(tile: DashboardTileRow, viewer: Viewer, ctx: GateContext, queues: readonly WorkQueueRow[]): boolean {
  if (tile.isActive === false) return false;
  // 🔒 No tile reaches an external session (piece 8; decision 0607): every one of them reads across companies — the queues, the feed, the two dashboards — and a tile scoped to one customer is not a tile that exists.
  if (externalSession(viewer, ctx)) return false;
  if (tile.permissionKey === ANY_QUEUE_KEY) return visibleQueues(queues, viewer, ctx).length > 0;
  if (isModule(tile.permissionKey)) return canReadModule(viewer, tile.permissionKey as Module, ctx);
  return canUseCapability(viewer, tile.permissionKey, ctx);
}

/**
 * The tiles for one viewer, in the seeded order unless their own arrangement
 * says otherwise. 🔨 A viewer failing a tile's key gets no tile and no
 * placeholder in its place.
 */
export function resolveTiles(
  tiles: readonly DashboardTileRow[],
  viewer: Viewer,
  ctx: GateContext,
  queues: readonly WorkQueueRow[],
  arrangement?: readonly string[] | null,
): DashboardTileRow[] {
  const visible = tiles.filter((tile) => tileIsVisible(tile, viewer, ctx, queues));
  if (!arrangement || arrangement.length === 0) return visible.sort((a, b) => a.sortRank - b.sortRank || a.key.localeCompare(b.key));
  const order = new Map(arrangement.map((key, index) => [key, index]));
  // A tile the arrangement never mentions keeps its seeded place at the end.
  return visible.sort((a, b) => (order.get(a.key) ?? Number.MAX_SAFE_INTEGER) - (order.get(b.key) ?? Number.MAX_SAFE_INTEGER) || a.sortRank - b.sortRank);
}

/**
 * 🔨 The Activity Feed is a mechanism, not a tile: every status change worth
 * surfacing writes one entry, and no module invents its own notification
 * surface. 💰 Four private reminder mechanisms were already folded into one
 * engine, and a fifth would be a mistake.
 */
export interface ActivityFeedEntryRow {
  id: string;
  occurredAt: string | Date;
  entryType: string;
  summary: string;
  actorPersonId?: string | null;
  audienceSystemRoleId?: string | null;
  audiencePersonId?: string | null;
  /** The record the entry names, where it names one (T2398): the on-call notice links to its job's Details view. */
  taggedRecordType?: string | null;
  taggedRecordId?: string | null;
}

export class ActivityFeedError extends Error {}

/** Exactly one of the two audience columns is always set. */
export function assertOneAudience(entry: Pick<ActivityFeedEntryRow, 'audienceSystemRoleId' | 'audiencePersonId'>): void {
  const role = entry.audienceSystemRoleId != null;
  const person = entry.audiencePersonId != null;
  if (role === person) throw new ActivityFeedError('an activity feed entry names a role or a person, never both and never neither');
}

/** The entries one viewer sees: their own, plus their role's. */
export function feedFor(entries: readonly ActivityFeedEntryRow[], viewer: Viewer): ActivityFeedEntryRow[] {
  return entries
    .filter((e) => e.audiencePersonId === viewer.personId || (e.audienceSystemRoleId != null && e.audienceSystemRoleId === viewer.role?.id))
    .sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime());
}

// ---- The seeded tiles, their keys and the width each declares ----

/**
 * The six seeded tiles' keys, named once so the seed, the resolver and the
 * screen cannot spell one of them differently (piece 12). Three arrived with
 * the dashboard, and three on 2026-09-20: the approvals queue the owner could
 * not reach, and the two report dashboards the owner asked to read from here.
 */
export const ACTIVITY_FEED_TILE = 'tile:activity-feed';
export const WORK_QUEUE_TILE = 'tile:work-queues';
export const MY_TODOS_TILE = 'tile:my-todos';
export const PENDING_APPROVALS_TILE = 'tile:pending-approvals';
export const PROJECT_PERFORMANCE_TILE = 'tile:project-performance';
export const SALES_REPORTING_TILE = 'tile:sales-reporting';

/**
 * 🔒 How many of the canvas's three columns each seeded tile declares, summing
 * to a whole number of rows so the seeded set fills the rows it stands on
 * (decision 0601). 🔨 A tile the map does not name declares one column, which
 * is what a later piece's tile gets until the map names it. 💰 The four seeded
 * spans summed to four on a three-column canvas, so the canvas widened whatever
 * landed last and the dashboard read as a ragged grid (product owner,
 * 2026-09-20).
 */
export const DASHBOARD_TILE_SPANS: Readonly<Record<string, 1 | 2 | 3>> = {
  [ACTIVITY_FEED_TILE]: 2,
  [WORK_QUEUE_TILE]: 1,
  [MY_TODOS_TILE]: 2,
  [PENDING_APPROVALS_TILE]: 1,
  [PROJECT_PERFORMANCE_TILE]: 1,
  [SALES_REPORTING_TILE]: 2,
};

export function dashboardTileSpan(key: string): 1 | 2 | 3 {
  return DASHBOARD_TILE_SPANS[key] ?? 1;
}

/** The spans of a set of tiles, summed; the seeded set's sum is a multiple of the column count. */
export function tileSpanSum(keys: readonly string[]): number {
  return keys.reduce((total, key) => total + dashboardTileSpan(key), 0);
}

// ---- The Pending Approvals tile's own numbers ----

/** One document type's share of the queue, as the tile draws it: the type and how many of it wait. */
export interface PendingApprovalsTypeCount {
  documentType: string;
  count: number;
}

/**
 * 🔒 What the Pending Approvals tile draws, and it is the queue page's own
 * answer counted (decision 0600): the rows this viewer's read lists, the ones
 * they may act on, and the count per document type. 💰 The tile and the queue
 * row counted every Pending document in the organisation instead, so the number
 * on the dashboard was larger than the page it opened (product owner,
 * 2026-09-20).
 */
export interface PendingApprovalsSummary {
  count: number;
  /** How many of them this viewer may decide, which is the group the page leads with. */
  mine: number;
  byType: readonly PendingApprovalsTypeCount[];
}

export const NO_PENDING_APPROVALS: PendingApprovalsSummary = { count: 0, mine: 0, byType: [] };

/**
 * The summary over the rows the queue's own read answered. 🔨 A type with
 * nothing waiting is left out, because the tile draws only the types with a
 * count; the order is the order the rows arrived in, which is the adapters'.
 */
export function pendingApprovalsSummary(rows: readonly { documentType: string; canAct: boolean }[]): PendingApprovalsSummary {
  const byType: PendingApprovalsTypeCount[] = [];
  for (const row of rows) {
    const held = byType.find((entry) => entry.documentType === row.documentType);
    if (held) held.count += 1;
    else byType.push({ documentType: row.documentType, count: 1 });
  }
  return { count: rows.length, mine: rows.filter((row) => row.canAct).length, byType };
}
